import {
  useEffect,
  useRef,
} from 'react';

import TreeStructuralRepairReview from '@/components/governor/TreeStructuralRepairReview';
import TreeHistoricalAuthorityPresentationErrorBoundary from '@/components/governor/TreeHistoricalAuthorityPresentationErrorBoundary';

import {
  publishCanonicalTreeHistoricalAuthorityPresentationObservability,
} from '@/governor/tree/CanonicalTreeHistoricalAuthorityPresentationObservability';

import {
  closeCanonicalTreeHistoricalAuthorityPresentationLifecycleEpisodes,
} from '@/governor/tree/CanonicalTreeHistoricalAuthorityPresentationObservabilityLifecycle';

import {
  startCanonicalTreeRetirementReadinessAuthenticatedEvidencePublicationRuntimeCoordinator,
} from '@/governor/tree/CanonicalTreeHistoricalAuthorityPresentationObservabilityLifecycleRetirementReadinessAuthenticatedEvidencePublicationRuntimeCoordinator';
import TreeSimulationWorkspace from '@/components/governor/TreeSimulationWorkspace';

import {
  useCanonicalTreeRegistryEvidence,
  type CanonicalTreeRegistryEvidenceReactSnapshot,
} from '@/hooks/governor/useCanonicalTreeRegistryEvidence';

import {
  resolveContainedCanonicalTreeHistoricalAuthorityPresentation,
  type CanonicalTreeContainedHistoricalAuthorityPresentation,
} from '@/governor/tree/CanonicalTreeHistoricalAuthorityPresentationFailureContainment';

import {
  useTreeRuntimeConvergenceEvidence,
  useTreeGeometryRuntimeEvidence,
  useTreeRuntimeLegacyAncestryCausalEvidence,
  useTreeRuntimeCanonicalBranchCounterfactualEvidence,
  useTreeRuntimeBranchLayoutInteractionEvidence,
  type TreeGeometryRuntimeLiveProjection,
  type TreeLegacyAncestryCausalLiveProjection,
  type TreeCanonicalBranchCounterfactualLiveProjection,
  type TreeBranchLayoutInteractionLiveProjection,
} from '@/hooks/governor/useTreeRuntimeConvergenceEvidence';

import {
  useTreeRuntimeMissingParentLifecycleOperationalObservability,
} from '@/hooks/governor/useTreeRuntimeMissingParentLifecycleOperationalObservability';

import type {
  TreeRuntimeConvergenceLiveProjection,
} from '@/governor/tree-runtime/TreeRuntimeConvergenceEvidenceProjection';

type TreeOperationsWorkspaceProps = {
  repairPlan: any;
  liveOutput: any;
  onSimulationChange?: () => void;
};

function factValue(
  value:
    string |
    number |
    boolean |
    null,
): string {
  if (value === null) {
    return 'Not available';
  }

  if (typeof value === 'boolean') {
    return value
      ? 'Yes'
      : 'No';
  }

  return String(value);
}

function registryLifecycleToneClass(
  snapshot:
    CanonicalTreeRegistryEvidenceReactSnapshot,
): string {
  if (
    snapshot.publisherLifecycle ===
    'PUBLISHER_ABSENT'
  ) {
    return 'border-slate-200 bg-slate-50 text-slate-900';
  }

  if (
    snapshot.publisherLifecycle ===
    'PUBLISHER_UNMOUNTED'
  ) {
    return snapshot.lastKnownSnapshotReadable
      ? 'border-amber-200 bg-amber-50 text-amber-950'
      : 'border-slate-300 bg-slate-100 text-slate-900';
  }

  if (
    snapshot.evidenceAvailability ===
    'EVIDENCE_AVAILABLE'
  ) {
    return 'border-emerald-200 bg-emerald-50 text-emerald-950';
  }

  if (
    snapshot.evidenceAvailability ===
    'EVIDENCE_STALE'
  ) {
    return 'border-amber-200 bg-amber-50 text-amber-950';
  }

  if (
    snapshot.evidenceAvailability ===
    'NO_AUTHENTICATED_OWNER'
  ) {
    return 'border-slate-200 bg-slate-50 text-slate-900';
  }

  if (
    snapshot.evidenceAvailability ===
    'EVIDENCE_UNAVAILABLE'
  ) {
    return 'border-sky-200 bg-sky-50 text-sky-950';
  }

  return 'border-slate-200 bg-slate-50 text-slate-900';
}

function registryLifecycleLabel(
  snapshot:
    CanonicalTreeRegistryEvidenceReactSnapshot,
): string {
  if (
    snapshot.publisherLifecycle ===
    'PUBLISHER_ABSENT'
  ) {
    return 'Publisher not observed';
  }

  if (
    snapshot.publisherLifecycle ===
    'PUBLISHER_UNMOUNTED'
  ) {
    return snapshot.lastKnownSnapshotReadable
      ? 'Publisher unmounted — last-known evidence retained'
      : 'Publisher unmounted';
  }

  if (
    snapshot.evidenceAvailability ===
    'EVIDENCE_AVAILABLE'
  ) {
    return 'Publisher active — evidence available';
  }

  if (
    snapshot.evidenceAvailability ===
    'EVIDENCE_STALE'
  ) {
    return 'Publisher active — evidence stale';
  }

  if (
    snapshot.evidenceAvailability ===
    'NO_AUTHENTICATED_OWNER'
  ) {
    return 'Publisher active — no authenticated owner';
  }

  if (
    snapshot.evidenceAvailability ===
    'EVIDENCE_UNAVAILABLE'
  ) {
    return 'Publisher active — evidence unavailable';
  }

  return 'Publisher lifecycle state unavailable';
}

function registryLifecycleDetail(
  snapshot:
    CanonicalTreeRegistryEvidenceReactSnapshot,
): string {
  if (
    snapshot.publisherLifecycle ===
    'PUBLISHER_ABSENT'
  ) {
    return 'No governed Tree evidence publisher has been observed during the current tab lifecycle.';
  }

  if (
    snapshot.publisherLifecycle ===
    'PUBLISHER_UNMOUNTED'
  ) {
    return snapshot.lastKnownSnapshotReadable
      ? 'The publisher is no longer mounted. A governed last-known sanitized summary remains readable for diagnostics only.'
      : 'The publisher is no longer mounted and no governed last-known summary is readable.';
  }

  if (
    snapshot.evidenceAvailability ===
    'NO_AUTHENTICATED_OWNER'
  ) {
    return 'The publisher is active, but no authenticated graph owner is present.';
  }

  if (
    snapshot.evidenceAvailability ===
    'EVIDENCE_AVAILABLE'
  ) {
    return 'A validated sanitized Tree evidence summary is available for diagnostic presentation.';
  }

  if (
    snapshot.evidenceAvailability ===
    'EVIDENCE_STALE'
  ) {
    return 'A validated sanitized summary remains available, but it is classified as stale.';
  }

  if (
    snapshot.evidenceAvailability ===
    'EVIDENCE_UNAVAILABLE'
  ) {
    return 'The publisher is active, but authoritative sanitized evidence is not currently available.';
  }

  return 'The registry lifecycle state cannot currently be presented.';
}

function TreeRegistryLifecyclePresentation({
  snapshot,
}: {
  snapshot:
    CanonicalTreeRegistryEvidenceReactSnapshot;
}) {
  const summaryReadable =
    snapshot.summary !== null &&
    (
      snapshot.publisherLifecycle ===
        'PUBLISHER_ACTIVE' ||
      snapshot.lastKnownSnapshotReadable
    );

  const summary =
    summaryReadable
      ? snapshot.summary
      : null;

  const lifecycleFacts = [
    [
      'Publisher lifecycle',
      snapshot.publisherLifecycle,
    ],
    [
      'Evidence availability',
      snapshot.evidenceAvailability,
    ],
    [
      'Authenticated owner present',
      snapshot.authenticatedOwnerPresent,
    ],
    [
      'Last-known readable',
      snapshot.lastKnownSnapshotReadable,
    ],
    [
      'Registry revision',
      snapshot.revision,
    ],
    [
      'Changed at',
      snapshot.changedAt,
    ],
  ] as const;

  const summaryFacts = summary
    ? [
        [
          'Source status',
          summary.status,
        ],
        [
          'Owner resolved',
          summary.ownerUserId !== null,
        ],
        [
          'Members represented',
          summary.memberCount,
        ],
        [
          'Gateway-ready evidence',
          summary.readyForGateway,
        ],
        [
          'Refresh reason',
          summary.refreshReason,
        ],
        [
          'Last attempt',
          summary.lastAttemptStatus,
        ],
        [
          'Adoption action',
          summary.action,
        ],
        [
          'Error present',
          summary.error !== null,
        ],
      ] as const
    : [];

  return (
    <section
      className={`mt-5 rounded-[2rem] border p-6 ${
        registryLifecycleToneClass(
          snapshot,
        )
      }`}
    >
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.18em]">
            Registry lifecycle evidence
          </p>

          <h2 className="mt-2 text-3xl font-bold">
            {registryLifecycleLabel(
              snapshot,
            )}
          </h2>

          <p className="mt-2 max-w-3xl text-sm">
            {registryLifecycleDetail(
              snapshot,
            )}
          </p>
        </div>

        <div className="rounded-xl border border-current/20 bg-white/70 px-4 py-3 text-sm">
          <p className="font-bold">
            Registry scope
          </p>

          <p className="mt-1 break-words">
            {snapshot.scope}
          </p>
        </div>
      </div>

      <div className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {lifecycleFacts.map(([
          label,
          value,
        ]) => (
          <div
            key={label}
            className="rounded-xl border border-current/20 bg-white/70 p-4"
          >
            <p className="text-xs font-bold uppercase tracking-wide opacity-70">
              {label}
            </p>

            <p className="mt-1 break-words text-sm font-bold">
              {factValue(value)}
            </p>
          </div>
        ))}
      </div>

      <div className="mt-5 rounded-2xl border border-current/20 bg-white/60 p-4">
        <p className="text-sm font-bold">
          Sanitized summary
        </p>

        {summary ? (
          <div className="mt-3 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            {summaryFacts.map(([
              label,
              value,
            ]) => (
              <div
                key={label}
                className="rounded-xl border border-current/20 bg-white/80 p-4"
              >
                <p className="text-xs font-bold uppercase tracking-wide opacity-70">
                  {label}
                </p>

                <p className="mt-1 break-words text-sm font-bold">
                  {factValue(value)}
                </p>
              </div>
            ))}
          </div>
        ) : (
          <p className="mt-2 text-sm opacity-75">
            No registry summary is readable under the current lifecycle state.
          </p>
        )}
      </div>

      <p className="mt-5 text-xs opacity-75">
        Read-only lifecycle diagnostic. Registry revision and timestamps describe publication changes only. They do not certify Tree health, runtime correctness, or release readiness.
      </p>
    </section>
  );
}

function historicalAuthorityToneClass(
  presentation:
    CanonicalTreeContainedHistoricalAuthorityPresentation,
): string {
  if (presentation.tone === 'neutral') {
    return 'border-slate-300 bg-slate-100 text-slate-950';
  }

  if (presentation.tone === 'confirmed') {
    return 'border-emerald-200 bg-emerald-50 text-emerald-950';
  }

  if (presentation.tone === 'informational') {
    return 'border-sky-200 bg-sky-50 text-sky-950';
  }

  return 'border-amber-200 bg-amber-50 text-amber-950';
}

function TreeHistoricalAuthorityPresentation({
  presentation,
}: {
  presentation:
    CanonicalTreeContainedHistoricalAuthorityPresentation;
}) {
  const facts = [
    [
      'Presentation state',
      presentation.presentationState,
    ],
    [
      'Current authority milestone',
      presentation.currentRepositoryAuthorityMilestone,
    ],
    [
      'Physical source state',
      presentation.currentPhysicalSourceState,
    ],
    [
      'Retired sources deleted',
      presentation.deletedHistoricalSourceCount,
    ],
    [
      'Current repository sources',
      presentation.physicalRepositorySourceCount,
    ],
    [
      'Active production references',
      presentation.activeProductionReferenceCount,
    ],
    [
      'Immutable archive authority',
      presentation.immutableArchiveAuthority,
    ],
    [
      'Historical verifiers preserved',
      presentation.historicalVerifierAuthorityPreserved,
    ],
    [
      'Historical documentation preserved',
      presentation.historicalDocumentationAuthorityPreserved,
    ],
    [
      'Historical authorities',
      presentation.historicalAuthorityCount,
    ],
    [
      'Current authorities',
      presentation.currentAuthorityCount,
    ],
    [
      'Feed generated at',
      presentation.sourceFeedGeneratedAt,
    ],
  ] as const;

  return (
    <section
      className={`mt-5 rounded-[2rem] border p-6 ${historicalAuthorityToneClass(
        presentation,
      )}`}
    >
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.18em]">
            Historical authority
          </p>

          <h2 className="mt-2 text-3xl font-bold">
            {presentation.label}
          </h2>

          <p className="mt-2 max-w-3xl text-sm">
            {presentation.detail}
          </p>
        </div>

        <div className="rounded-xl border border-current/20 bg-white/70 px-4 py-3 text-sm">
          <p className="font-bold">
            Projection milestone
          </p>

          <p className="mt-1">
            {presentation.milestone}
          </p>
        </div>
      </div>

      <div className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {facts.map(([
          label,
          value,
        ]) => (
          <div
            key={label}
            className="rounded-xl border border-current/20 bg-white/70 p-4"
          >
            <p className="text-xs font-bold uppercase tracking-wide opacity-70">
              {label}
            </p>

            <p className="mt-1 break-words text-sm font-bold">
              {factValue(value)}
            </p>
          </div>
        ))}
      </div>

      <p className="mt-5 text-xs opacity-75">
        Internal Governor presentation only. This historical repository-authority projection does not certify Tree health, runtime correctness, operational certification, deployment readiness, or release readiness.
      </p>
    </section>
  );
}

function geometryRuntimeToneClass(
  projection:
    TreeGeometryRuntimeLiveProjection,
): string {
  if (
    projection.availability ===
    'NOT_OBSERVED'
  ) {
    return 'border-slate-200 bg-slate-50 text-slate-900';
  }

  if (
    projection.availability ===
    'INVALID_EVIDENCE'
  ) {
    return 'border-rose-200 bg-rose-50 text-rose-950';
  }

  if (
    projection.status ===
    'FAIL'
  ) {
    return 'border-rose-200 bg-rose-50 text-rose-950';
  }

  return 'border-emerald-200 bg-emerald-50 text-emerald-950';
}

function TreeGeometryRuntimeEvidencePresentation({
  projection,
}: {
  projection:
    TreeGeometryRuntimeLiveProjection;
}) {
  const nonzeroFatalRules =
    projection.fatalRuleCounts
      ? Object.entries(
          projection.fatalRuleCounts,
        )
          .filter(
            ([, count]) =>
              count > 0,
          )
          .sort(
            ([left], [right]) =>
              left.localeCompare(
                right,
              ),
          )
      : [];

  const facts = [
    [
      'Availability',
      projection.availability,
    ],
    [
      'Geometry status',
      projection.status,
    ],
    [
      'Failure-source classification',
      projection
        .failureSourceClassificationAvailability,
    ],
    [
      'Issue count',
      projection.issueCount,
    ],
    [
      'Fatal issues',
      projection.fatalIssueCount,
    ],
    [
      'Warnings',
      projection.warningIssueCount,
    ],
    [
      'Classified fatal issues',
      projection.fatalKnownIssueCount,
    ],
    [
      'Unknown fatal issues',
      projection.fatalUnknownIssueCount,
    ],
    [
      'Informational issues',
      projection.informationalIssueCount,
    ],
    [
      'Pass sentinels',
      projection.passIssueCount,
    ],
    [
      'Unclassified severities',
      projection.unclassifiedSeverityCount,
    ],
    [
      'Visible cards',
      projection.cardCount,
    ],
    [
      'Canonical edges',
      projection.edgeCount,
    ],
    [
      'Parent connectors missing',
      projection
        .parentConnectorMissingCount,
    ],
    [
      'Parent connectors unrouted',
      projection
        .parentConnectorUnroutedCount,
    ],
    [
      'Rejected routes',
      projection.routeRejectedCount,
    ],
    [
      'Rejected: nonadjacent generation',
      projection
        .routeRejectedNonadjacentGenerationCount,
    ],
    [
      'Rejected: root boundary',
      projection
        .routeRejectedRootBoundaryCount,
    ],
    [
      'Rejected: obstacle intersection',
      projection
        .routeRejectedObstacleIntersectionCount,
    ],
    [
      'Rejected: unclassified',
      projection
        .routeRejectedUnclassifiedCount,
    ],
    [
      'ROUTED without route geometry',
      projection.routeMissingCount,
    ],
    [
      'Parent endpoint authority failures',
      projection
        .parentEndpointAuthorityFailureCount,
    ],
    [
      'Observed at',
      projection.observedAt,
    ],
  ] as const;

  return (
    <section
      data-testid="tree-geometry-runtime-evidence"
      data-tree-geometry-runtime-availability={
        projection.availability
      }
      data-tree-geometry-runtime-status={
        projection.status ??
        'NOT_OBSERVED'
      }
      data-tree-geometry-failure-source-availability={
        projection
          .failureSourceClassificationAvailability
      }
      data-tree-geometry-fatal-unknown-count={
        projection.fatalUnknownIssueCount ??
        'NOT_OBSERVED'
      }
      data-tree-geometry-route-unclassified-count={
        projection
          .routeRejectedUnclassifiedCount ??
        'NOT_OBSERVED'
      }
      className={`mt-5 rounded-[2rem] border p-6 ${geometryRuntimeToneClass(
        projection,
      )}`}
    >
      <p className="text-xs font-bold uppercase tracking-[0.18em]">
        Live Tree geometry
      </p>

      <h2 className="mt-2 text-3xl font-bold">
        {projection.availability ===
        'NOT_OBSERVED'
          ? 'Geometry runtime not observed'
          : projection.availability ===
              'INVALID_EVIDENCE'
            ? 'Geometry evidence invalid'
            : projection.status ===
                'FAIL'
              ? 'Tree geometry failed'
              : 'Tree geometry passed'}
      </h2>

      <p className="mt-2 max-w-3xl text-sm">
        {projection.availability ===
        'NOT_OBSERVED'
          ? 'Open the authenticated Full Tree, allow the canonical geometry to render, then navigate to Command Center in the same tab.'
          : projection.availability ===
              'INVALID_EVIDENCE'
            ? 'A Tree geometry publication was observed but failed aggregate evidence validation. Repair authority remains blocked.'
            : 'Identity-suppressed aggregate geometry evidence from the authenticated Tree. No member IDs, edge IDs, raw route points, or family identities are published here.'}
      </p>

      <div className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {facts.map(([
          label,
          value,
        ]) => (
          <div
            key={label}
            className="rounded-xl border border-current/20 bg-white/70 p-4"
          >
            <p className="text-xs font-bold uppercase tracking-wide opacity-70">
              {label}
            </p>

            <p className="mt-1 break-words text-sm font-bold">
              {factValue(value)}
            </p>
          </div>
        ))}
      </div>

      {projection.availability ===
        'OBSERVED' &&
        projection
          .failureSourceClassificationAvailability ===
          'NOT_PUBLISHED' && (
        <div
          data-testid="tree-geometry-failure-source-not-published"
          className="mt-5 rounded-2xl border border-current/20 bg-white/70 p-4"
        >
          <p className="text-xs font-bold uppercase tracking-[0.14em] opacity-70">
            Failure-source classification not published
          </p>

          <p className="mt-2 text-xs opacity-75">
            Legacy 003G3E geometry evidence remains valid geometry evidence, but it grants no 003G3E1 failure-source classification or source-repair localization authority.
          </p>
        </div>
      )}

      {projection
        .failureSourceClassificationAvailability ===
        'OBSERVED' && (
        <div
          data-testid="tree-geometry-fatal-rule-distribution"
          className="mt-5 rounded-2xl border border-current/20 bg-white/70 p-4"
        >
          <p className="text-xs font-bold uppercase tracking-[0.14em] opacity-70">
            Fatal rule distribution
          </p>

          <p className="mt-2 text-xs opacity-75">
            Aggregate rule counts only. No member IDs, edge keys, obstacle identities, coordinates, or raw issue details are displayed.
          </p>

          {nonzeroFatalRules.length === 0 ? (
            <p className="mt-3 text-sm font-bold">
              No fatal rule counts observed.
            </p>
          ) : (
            <div className="mt-3 grid gap-2 sm:grid-cols-2">
              {nonzeroFatalRules.map(
                ([rule, count]) => (
                  <div
                    key={rule}
                    className="rounded-xl border border-current/20 bg-white/80 p-3"
                  >
                    <p className="break-all text-[11px] font-bold">
                      {rule}
                    </p>
                    <p className="mt-1 text-lg font-bold">
                      {count}
                    </p>
                  </div>
                ),
              )}
            </div>
          )}
        </div>
      )}

      <p className="mt-5 text-xs opacity-75">
        Read-only 003G3E1 failure-source classification. Parent-connector completeness uses the same visible structural parent-edge obligation enforced by whole-tree stability containment. Unknown fatal rules and unclassified route rejections fail closed. No layout mutation, database write, connector fabrication, or repair execution occurs here.
      </p>
    </section>
  );
}

function legacyAncestryCausalToneClass(
  projection:
    TreeLegacyAncestryCausalLiveProjection,
): string {
  if (
    projection.availability ===
    'NOT_PUBLISHED'
  ) {
    return 'border-slate-200 bg-slate-50 text-slate-900';
  }

  if (
    projection.availability ===
    'INVALID_EVIDENCE'
  ) {
    return 'border-rose-200 bg-rose-50 text-rose-950';
  }

  if (
    (
      projection
        .rootBoundaryAnyDivergentCount ??
      0
    ) > 0 ||
    (
      projection
        .horizontalAnyDivergentCount ??
      0
    ) > 0
  ) {
    return 'border-amber-200 bg-amber-50 text-amber-950';
  }

  return 'border-sky-200 bg-sky-50 text-sky-950';
}

function TreeLegacyAncestryCausalEvidencePresentation({
  projection,
}: {
  projection:
    TreeLegacyAncestryCausalLiveProjection;
}) {
  const failureReasons =
    projection
      .viewerTerminalFailureReasonCounts
      ? Object.entries(
          projection
            .viewerTerminalFailureReasonCounts,
        ).filter(
          ([, count]) =>
            count > 0,
        )
      : [];

  const horizontal =
    projection.horizontal
      ? Object.entries(
          projection.horizontal,
        )
      : [];

  const facts = [
    [
      'Availability',
      projection.availability,
    ],
    [
      'Geometry observation coherent',
      projection
        .coherentGeometryObservation,
    ],
    [
      'Geometry coherence delta ms',
      projection
        .geometryCoherenceDeltaMs,
    ],
    [
      'Legacy branch fallback selected',
      projection
        .branchLegacyFallbackSelected,
    ],
    [
      'Legacy layout fallback selected',
      projection
        .layoutLegacyFallbackSelected,
    ],
    [
      'Root-boundary rejected',
      projection
        .rootBoundaryRejectedCount,
    ],
    [
      'Root-boundary maternal',
      projection
        .rootBoundaryMaternalCount,
    ],
    [
      'Root-boundary paternal',
      projection
        .rootBoundaryPaternalCount,
    ],
    [
      'Root-boundary other root',
      projection
        .rootBoundaryOtherRootCount,
    ],
    [
      'Viewer-terminal candidates',
      projection
        .rootBoundaryViewerTerminalCandidateCount,
    ],
    [
      'Ordinary ancestry routes',
      projection
        .rootBoundaryOrdinaryCount,
    ],
    [
      'Viewer-terminal candidates ineligible',
      projection
        .rootBoundaryCandidateIneligibleCount,
    ],
    [
      'Eligible-but-rejected contradictions',
      projection
        .rootBoundaryEligibleContradictionCount,
    ],
    [
      'Root cohort: branch divergent',
      projection
        .rootBoundaryBranchDivergentCount,
    ],
    [
      'Root cohort: layout divergent',
      projection
        .rootBoundaryLayoutDivergentCount,
    ],
    [
      'Root cohort: any divergent',
      projection
        .rootBoundaryAnyDivergentCount,
    ],
    [
      'Root cohort: indeterminate',
      projection
        .rootBoundaryIndeterminateCount,
    ],
    [
      'Root cohort: parity',
      projection
        .rootBoundaryParityCount,
    ],
    [
      'Horizontal violations',
      projection
        .horizontalViolationCount,
    ],
    [
      'Horizontal divergent violations',
      projection
        .horizontalAnyDivergentCount,
    ],
    [
      'Invalid private joins',
      projection.invalidJoinCount,
    ],
    [
      'Upstream authority proven',
      projection
        .upstreamAuthorityProven,
    ],
    [
      'Overlap is causation',
      projection.overlapIsCausation,
    ],
    [
      'Causal attribution allowed',
      projection
        .causalAttributionAllowed,
    ],
    [
      'Source mutation allowed',
      projection
        .sourceMutationAllowed,
    ],
    [
      'Observed at',
      projection.observedAt,
    ],
    [
      'Geometry observed at',
      projection.geometryObservedAt,
    ],
  ] as const;

  return (
    <section
      data-testid="tree-legacy-ancestry-causal-evidence"
      data-tree-legacy-causal-availability={
        projection.availability
      }
      data-tree-legacy-causal-coherent={
        projection
          .coherentGeometryObservation
      }
      data-tree-legacy-causal-source-mutation-allowed={
        projection
          .sourceMutationAllowed
      }
      className={`mt-5 rounded-[2rem] border p-6 ${legacyAncestryCausalToneClass(
        projection,
      )}`}
    >
      <p className="text-xs font-bold uppercase tracking-[0.18em]">
        Legacy ancestry causal localization
      </p>

      <h2 className="mt-2 text-3xl font-bold">
        {projection.availability ===
        'NOT_PUBLISHED'
          ? 'Causal localization not published'
          : projection.availability ===
              'INVALID_EVIDENCE'
            ? 'Causal evidence invalid'
            : 'Causal localization observed'}
      </h2>

      <p className="mt-2 max-w-3xl text-sm">
        {projection.availability ===
        'NOT_PUBLISHED'
          ? 'Open the authenticated Full Tree and allow the current 003G3E2 observation to publish in this tab.'
          : projection.availability ===
              'INVALID_EVIDENCE'
            ? 'The causal publication failed schema, privacy, arithmetic, or exact geometry-observation coherence validation. Source mutation remains blocked.'
            : 'Aggregate-only comparison of the failing geometry cohort against legacy and canonical branch/layout authority. Private member and edge identities were used only for the in-memory join and are not published.'}
      </p>

      <div className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {facts.map(([
          label,
          value,
        ]) => (
          <div
            key={label}
            className="rounded-xl border border-current/20 bg-white/70 p-4"
          >
            <p className="text-xs font-bold uppercase tracking-wide opacity-70">
              {label}
            </p>

            <p className="mt-1 break-words text-sm font-bold">
              {factValue(value)}
            </p>
          </div>
        ))}
      </div>

      {projection.availability ===
        'OBSERVED' &&
        horizontal.length > 0 && (
        <div
          data-testid="tree-legacy-ancestry-horizontal-distribution"
          className="mt-5 rounded-2xl border border-current/20 bg-white/70 p-4"
        >
          <p className="text-sm font-bold">
            Horizontal failure cohort
          </p>

          <p className="mt-1 text-xs opacity-75">
            Aggregate violation and divergence counts only.
          </p>

          <div className="mt-3 grid gap-3 xl:grid-cols-2">
            {horizontal.map(([
              rule,
              counts,
            ]) => (
              <div
                key={rule}
                className="rounded-xl border border-current/20 bg-white/80 p-4"
              >
                <p className="break-all text-[11px] font-bold">
                  {rule}
                </p>

                <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                  <p>
                    Violations: {counts.violationCount}
                  </p>
                  <p>
                    Any divergent: {counts.anyDivergentCount}
                  </p>
                  <p>
                    Branch divergent: {counts.branchDivergentCount}
                  </p>
                  <p>
                    Layout divergent: {counts.layoutDivergentCount}
                  </p>
                  <p>
                    Indeterminate: {counts.indeterminateCount}
                  </p>
                  <p>
                    Parity: {counts.parityCount}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {projection.availability ===
        'OBSERVED' &&
        failureReasons.length > 0 && (
        <div
          data-testid="tree-legacy-ancestry-viewer-terminal-failures"
          className="mt-5 rounded-2xl border border-current/20 bg-white/70 p-4"
        >
          <p className="text-sm font-bold">
            Viewer-terminal ineligibility reasons
          </p>

          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            {failureReasons.map(([
              reason,
              count,
            ]) => (
              <div
                key={reason}
                className="rounded-xl border border-current/20 bg-white/80 p-3"
              >
                <p className="break-all text-[11px] font-bold">
                  {reason}
                </p>
                <p className="mt-1 text-lg font-bold">
                  {count}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      <p className="mt-5 text-xs opacity-75">
        Read-only 003G3E2 causal localization. Cohort overlap is not causation. No source mutation, router relaxation, layout-coordinate change, database write, Tree-record repair, or automatic repair is authorized by this panel.
      </p>
    </section>
  );
}



function branchLayoutInteractionToneClass(
  projection:
    TreeBranchLayoutInteractionLiveProjection,
): string {
  if (
    projection.availability ===
      'NOT_PUBLISHED'
  ) {
    return 'border-slate-200 bg-slate-50 text-slate-900';
  }

  if (
    projection.availability ===
      'INVALID_EVIDENCE'
  ) {
    return 'border-rose-200 bg-rose-50 text-rose-950';
  }

  if (
    (projection.cellC?.introducedFatalCount ?? 0) > 0 ||
    (projection.cellD?.introducedFatalCount ?? 0) > 0
  ) {
    return 'border-amber-200 bg-amber-50 text-amber-950';
  }

  return 'border-sky-200 bg-sky-50 text-sky-950';
}

function TreeBranchLayoutInteractionPresentation({
  projection,
}: {
  projection:
    TreeBranchLayoutInteractionLiveProjection;
}) {
  const display = (
    value:
      string |
      number |
      boolean |
      null |
      undefined,
  ) =>
    value === null ||
    value === undefined
      ? 'NOT_OBSERVED'
      : String(value);

  const cells = [
    {
      cell: 'A',
      role: 'Production control',
      fatal:
        projection.cellA?.fatalCount ?? null,
      resolved: null,
      persisted: null,
      introduced: null,
    },
    {
      cell: 'B',
      role: 'Semantic-only reference',
      fatal:
        projection.cellB?.shadowFatalCount ?? null,
      resolved:
        projection.cellB?.resolvedFatalCount ?? null,
      persisted:
        projection.cellB?.persistedFatalCount ?? null,
      introduced:
        projection.cellB?.introducedFatalCount ?? null,
    },
    {
      cell: 'C',
      role: 'Layout-only shadow',
      fatal:
        projection.cellC?.shadowFatalCount ?? null,
      resolved:
        projection.cellC?.resolvedFatalCount ?? null,
      persisted:
        projection.cellC?.persistedFatalCount ?? null,
      introduced:
        projection.cellC?.introducedFatalCount ?? null,
    },
    {
      cell: 'D',
      role: 'Combined shadow',
      fatal:
        projection.cellD?.shadowFatalCount ?? null,
      resolved:
        projection.cellD?.resolvedFatalCount ?? null,
      persisted:
        projection.cellD?.persistedFatalCount ?? null,
      introduced:
        projection.cellD?.introducedFatalCount ?? null,
    },
  ] as const;

  const facts = [
    ['Availability', projection.availability],
    [
      'Geometry observation coherent',
      projection.coherentGeometryObservation,
    ],
    [
      '003G3E3 B observation coherent',
      projection.coherentBranchCounterfactualObservation,
    ],
    ['Control parity', projection.controlParity],
    ['Control world parity', projection.controlWorldParity],
    ['Control failure parity', projection.controlFailureParity],
    ['B reference coherence', projection.cellBReferenceCoherent],
    [
      'Semantic substitutions',
      projection.semanticSubstitutedMemberCount,
    ],
    [
      'Semantic indeterminate members',
      projection.semanticIndeterminateMemberCount,
    ],
    [
      'Layout substitutions',
      projection.layoutSubstitutedMemberCount,
    ],
    [
      'Layout indeterminate members',
      projection.layoutIndeterminateMemberCount,
    ],
    ['Source mutation allowed', projection.sourceMutationAllowed],
    [
      'Causal attribution allowed',
      projection.causalAttributionAllowed,
    ],
    ['Repair authorized', projection.repairAuthorized],
    [
      'Legacy retirement authorized',
      projection.legacyRetirementAuthorized,
    ],
    [
      'Production authority changed',
      projection.productionAuthorityChanged,
    ],
  ] as const;

  return (
    <section
      data-testid="tree-branch-layout-interaction-evidence"
      data-tree-branch-layout-availability={
        projection.availability
      }
      className={
        'mt-5 rounded-[2rem] border p-6 ' +
        branchLayoutInteractionToneClass(
          projection,
        )
      }
    >
      <p className="text-xs font-bold uppercase tracking-[0.18em]">
        Branch / layout interaction counterfactual
      </p>

      <h2 className="mt-2 text-3xl font-bold">
        {projection.availability ===
        'NOT_PUBLISHED'
          ? 'Four-cell evidence not published'
          : projection.availability ===
              'INVALID_EVIDENCE'
            ? 'Four-cell evidence invalid'
            : 'Four-cell aggregate evidence observed'}
      </h2>

      <p className="mt-2 max-w-4xl text-sm">
        {projection.availability ===
        'NOT_PUBLISHED'
          ? 'No coherent authenticated 003G3E4 observation is available in this Governor session.'
          : projection.availability ===
              'INVALID_EVIDENCE'
            ? 'The A/B/C/D evidence failed governed validation. No causal conclusion, repair authorization, or legacy retirement is permitted.'
            : 'A is production control, B is the 003G3E3 semantic-only reference, C is layout-only shadow, and D is combined shadow. OBSERVED describes evidence coherence only; it does not establish C or D as a production repair.'}
      </p>

      <div className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {cells.map(cell => (
          <div
            key={cell.cell}
            className="rounded-2xl border border-current/20 bg-white/75 p-4"
          >
            <p className="text-xs font-bold uppercase tracking-wide opacity-70">
              Cell {cell.cell}
            </p>

            <p className="mt-1 text-sm font-bold">
              {cell.role}
            </p>

            <div className="mt-3 space-y-1 text-xs">
              <p>Fatal: {display(cell.fatal)}</p>
              <p>Resolved: {display(cell.resolved)}</p>
              <p>Persisted: {display(cell.persisted)}</p>
              <p>Introduced: {display(cell.introduced)}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {facts.map(([label, value]) => (
          <div
            key={label}
            className="rounded-xl border border-current/20 bg-white/70 p-4"
          >
            <p className="text-xs font-bold uppercase tracking-wide opacity-70">
              {label}
            </p>

            <p className="mt-1 break-words text-sm font-bold">
              {display(value)}
            </p>
          </div>
        ))}
      </div>

      <p className="mt-5 text-xs opacity-75">
        Aggregate Governor evidence only. No private family identity evidence, source mutation, Tree-record repair, causal attribution, production-authority change, or legacy retirement is authorized by this panel.
      </p>
    </section>
  );
}


function canonicalBranchCounterfactualToneClass(
  projection:
    TreeCanonicalBranchCounterfactualLiveProjection,
): string {
  if (
    projection.availability ===
    'NOT_PUBLISHED'
  ) {
    return 'border-slate-200 bg-slate-50 text-slate-900';
  }

  if (
    projection.availability ===
    'INVALID_EVIDENCE'
  ) {
    return 'border-rose-200 bg-rose-50 text-rose-950';
  }

  if (
    (
      projection.introducedFatalCount ??
      0
    ) > 0
  ) {
    return 'border-amber-200 bg-amber-50 text-amber-950';
  }

  return 'border-sky-200 bg-sky-50 text-sky-950';
}

function TreeCanonicalBranchCounterfactualPresentation({
  projection,
}: {
  projection:
    TreeCanonicalBranchCounterfactualLiveProjection;
}) {
  const horizontal =
    projection.horizontal
      ? Object.entries(
          projection.horizontal,
        )
      : [];

  const root =
    projection.rootBoundary;

  const facts = [
    ['Availability', projection.availability],
    ['Geometry observation coherent', projection.coherentGeometryObservation],
    ['003G3E2 causal observation coherent', projection.coherentCausalObservation],
    ['Control parity', projection.controlParity],
    ['Control world parity', projection.controlWorldParity],
    ['Control failure parity', projection.controlFailureParity],
    ['Control fatal failures', projection.controlFatalCount],
    ['Shadow fatal failures', projection.shadowFatalCount],
    ['Resolved control failures', projection.resolvedFatalCount],
    ['Persisted control failures', projection.persistedFatalCount],
    ['New shadow failures', projection.introducedFatalCount],
    ['Indeterminate control failures', projection.indeterminateControlFatalCount],
    ['Root-boundary control', root?.control ?? null],
    ['Root-boundary resolved', root?.resolved ?? null],
    ['Root-boundary persisted', root?.persisted ?? null],
    ['Root-boundary introduced', root?.introduced ?? null],
    ['Root-boundary indeterminate', root?.indeterminate ?? null],
    ['Horizontal control', projection.horizontalControlCount],
    ['Horizontal resolved', projection.horizontalResolvedCount],
    ['Horizontal persisted', projection.horizontalPersistedCount],
    ['Horizontal introduced', projection.horizontalIntroducedCount],
    ['Horizontal indeterminate', projection.horizontalIndeterminateCount],
    ['Canonical substitutions', projection.substitutedMemberCount],
    ['Indeterminate members', projection.indeterminateMemberCount],
    ['Invalid joins', projection.invalidJoinCount],
    ['Source mutation allowed', projection.sourceMutationAllowed],
    ['Causal attribution allowed', projection.causalAttributionAllowed],
    ['Legacy retirement authorized', projection.legacyRetirementAuthorized],
    ['Production authority changed', projection.productionAuthorityChanged],
    ['Observed at', projection.observedAt],
    ['Geometry observed at', projection.geometryObservedAt],
    ['Causal observed at', projection.causalObservedAt],
  ] as const;

  return (
    <section
      data-testid="tree-canonical-branch-counterfactual-evidence"
      data-tree-counterfactual-availability={
        projection.availability
      }
      data-tree-counterfactual-control-parity={
        projection.controlParity ??
        'NOT_OBSERVED'
      }
      data-tree-counterfactual-source-mutation-allowed={
        projection.sourceMutationAllowed
      }
      className={`mt-5 rounded-[2rem] border p-6 ${canonicalBranchCounterfactualToneClass(
        projection,
      )}`}
    >
      <p className="text-xs font-bold uppercase tracking-[0.18em]">
        Canonical branch counterfactual shadow
      </p>

      <h2 className="mt-2 text-3xl font-bold">
        {projection.availability ===
        'NOT_PUBLISHED'
          ? 'Counterfactual not published'
          : projection.availability ===
              'INVALID_EVIDENCE'
            ? 'Counterfactual evidence invalid'
            : 'Paired control / shadow observed'}
      </h2>

      <p className="mt-2 max-w-3xl text-sm">
        {projection.availability ===
        'NOT_PUBLISHED'
          ? 'Open the authenticated Full Tree and allow the 003G3E3 paired control/shadow computation to publish in this tab.'
          : projection.availability ===
              'INVALID_EVIDENCE'
            ? 'The counterfactual failed exact control, geometry, causal-coherence, arithmetic, schema, or privacy validation. No causal conclusion or source mutation is authorized.'
            : 'Read-only paired comparison using the same Tree input. Canonical semantic branch roots are substituted only where canonical evidence is positive; production rendering remains unchanged.'}
      </p>

      <div className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {facts.map(([
          label,
          value,
        ]) => (
          <div
            key={label}
            className="rounded-xl border border-current/20 bg-white/70 p-4"
          >
            <p className="text-xs font-bold uppercase tracking-wide opacity-70">
              {label}
            </p>

            <p className="mt-1 break-words text-sm font-bold">
              {factValue(value)}
            </p>
          </div>
        ))}
      </div>

      {projection.availability ===
        'OBSERVED' &&
        horizontal.length > 0 && (
        <div className="mt-5 rounded-2xl border border-current/20 bg-white/70 p-4">
          <p className="text-sm font-bold">
            Exact horizontal cohort counterfactual
          </p>

          <p className="mt-1 text-xs opacity-75">
            Aggregate counts only. Private member and edge signatures remain inside the in-memory comparison.
          </p>

          <div className="mt-3 grid gap-3 xl:grid-cols-2">
            {horizontal.map(([
              rule,
              counts,
            ]) => (
              <div
                key={rule}
                className="rounded-xl border border-current/20 bg-white/80 p-4"
              >
                <p className="break-all text-[11px] font-bold">
                  {rule}
                </p>

                <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                  <p>Control: {counts.control}</p>
                  <p>Resolved: {counts.resolved}</p>
                  <p>Persisted: {counts.persisted}</p>
                  <p>Introduced: {counts.introduced}</p>
                  <p>Indeterminate: {counts.indeterminate}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <p className="mt-5 text-xs opacity-75">
        Read-only 003G3E3 counterfactual evidence. Counterfactual improvement may support a later causal classification, but this panel grants no legacy retirement, source mutation, router change, spacing change, database write, Tree-record repair, or automatic repair authority.
      </p>
    </section>
  );
}


function convergenceToneClass(
  projection:
    TreeRuntimeConvergenceLiveProjection,
): string {
  if (projection.status === 'READY') {
    return 'border-emerald-200 bg-emerald-50 text-emerald-950';
  }

  if (projection.status === 'DEGRADED') {
    return 'border-amber-200 bg-amber-50 text-amber-950';
  }

  if (projection.status === 'UNSAFE') {
    return 'border-rose-200 bg-rose-50 text-rose-950';
  }

  return 'border-slate-200 bg-slate-50 text-slate-900';
}

function TreeRuntimeConvergencePresentation({
  projection,
}: {
  projection:
    TreeRuntimeConvergenceLiveProjection;
}) {
  const facts = [
    [
      'Live status',
      projection.status,
    ],
    [
      'Enforcement readiness',
      projection.enforcementReadiness,
    ],
    [
      'Convergence status',
      projection.convergenceStatus,
    ],
    [
      'Containment action',
      projection.containmentAction,
    ],
    [
      'Blocker count',
      projection.blockerCount,
    ],
    [
      'Visible members',
      projection.productionVisibleMemberCount,
    ],
    [
      'Missing projections',
      projection.missingProjectionCount,
    ],
    [
      'Missing geometry roles',
      projection.missingGeometryRoleCount,
    ],
    [
      'Missing connectors',
      projection.missingConnectorCount,
    ],
    [
      'Observed at',
      projection.observedAt,
    ],
  ] as const;

  return (
    <section
      data-testid="tree-runtime-convergence-live-evidence"
      data-tree-runtime-convergence-live-status={
        projection.status
      }
      data-tree-runtime-enforcement-readiness={
        projection.enforcementReadiness
      }
      className={`mt-5 rounded-[2rem] border p-6 ${convergenceToneClass(
        projection,
      )}`}
    >
      <p className="text-xs font-bold uppercase tracking-[0.18em]">
        Live Tree convergence
      </p>

      <h2 className="mt-2 text-3xl font-bold">
        {projection.status === 'NOT_OBSERVED'
          ? 'No authenticated Tree observation'
          : `Tree runtime ${projection.status.toLowerCase()}`}
      </h2>

      <p className="mt-2 max-w-3xl text-sm">
        {projection.status === 'READY'
          ? 'A same-tab authenticated Tree observation is available. The result is eligible for a later enforcement review, but no enforcement is active.'
          : projection.status === 'DEGRADED'
            ? 'The latest authenticated Tree observation is degraded. Enforcement remains blocked until the reported authority blockers are resolved.'
            : projection.status === 'UNSAFE'
              ? 'The latest authenticated Tree observation is unsafe. Enforcement remains blocked and no geometry or family data has been changed.'
              : 'Open the authenticated Tree in this tab, allow it to mount, then navigate to Command Center without reloading the browser.'}
      </p>

      <div className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {facts.map(([
          label,
          value,
        ]) => (
          <div
            key={label}
            className="rounded-xl border border-current/20 bg-white/70 p-4"
          >
            <p className="text-xs font-bold uppercase tracking-wide opacity-70">
              {label}
            </p>

            <p className="mt-1 break-words text-sm font-bold">
              {factValue(value)}
            </p>
          </div>
        ))}
      </div>

      <div className="mt-5 rounded-xl border border-current/20 bg-white/70 p-4">
        <p className="text-sm font-bold">
          Selector diagnostics
        </p>

        <div className="mt-3 grid gap-2 sm:grid-cols-2">
          {Object.entries(
            projection.selectorDiagnostics,
          ).map(([key, value]) => (
            <div
              key={key}
              className="rounded-lg border border-current/15 bg-white/60 p-3"
            >
              <p className="break-words text-[11px] font-bold uppercase tracking-wide opacity-70">
                {key}
              </p>

              <p className="mt-1 text-sm font-bold">
                {value}
              </p>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-5 rounded-xl border border-current/20 bg-white/70 p-4">
        <p className="text-sm font-bold">
          Selector discrepancy classification
        </p>

        <p className="mt-1 text-xs opacity-75">
          Aggregate reason-code counts only. No member identity or raw selector evidence is displayed.
        </p>

        <div className="mt-3 grid gap-2 sm:grid-cols-2">
          {Object.entries(
            projection
              .selectorDiscrepancyClassification,
          ).map(([key, value]) => (
            <div
              key={key}
              className="rounded-lg border border-current/15 bg-white/60 p-3"
            >
              <p className="break-words text-[11px] font-bold uppercase tracking-wide opacity-70">
                {key}
              </p>

              <p className="mt-1 text-sm font-bold">
                {value}
              </p>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-5 rounded-xl border border-current/20 bg-white/70 p-4">
        <p className="text-sm font-bold">
          Selector discrepancy source classification
        </p>

        <p className="mt-1 text-xs opacity-75">
          Aggregate terminal-edge and missing-reference source counts only. No member identity, edge identity, source-reference identity, or raw compatibility reason is displayed.
        </p>

        <div className="mt-3 grid gap-2 sm:grid-cols-2">
          {Object.entries(
            projection
              .selectorDiscrepancySourceClassification,
          ).map(([key, value]) => (
            <div
              key={key}
              className="rounded-lg border border-current/15 bg-white/60 p-3"
            >
              <p className="break-words text-[11px] font-bold uppercase tracking-wide opacity-70">
                {key}
              </p>

              <p className="mt-1 text-sm font-bold">
                {value}
              </p>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-5 rounded-xl border border-current/20 bg-white/70 p-4">
        <p className="text-sm font-bold">
          Blockers
        </p>

        <p className="mt-2 break-words text-sm">
          {projection.blockers.length > 0
            ? projection.blockers.join(', ')
            : 'None observed'}
        </p>
      </div>

      <p className="mt-5 text-xs opacity-75">
        Same-tab, session-recoverable, read-only diagnostic. Selector, discrepancy, and source-classification counts contain no member identity, edge identity, source-reference identity, or raw evidence. No containment enforcement, geometry suppression, repair execution, database persistence, or family-record mutation occurs here.
      </p>
    </section>
  );
}


function lifecycleObservabilityToneClass(
  snapshot:
    ReturnType<
      typeof useTreeRuntimeMissingParentLifecycleOperationalObservability
    >,
): string {
  if (snapshot.availability === 'NOT_OBSERVED') {
    return 'border-slate-200 bg-slate-50 text-slate-900';
  }

  if (snapshot.severity === 'fail') {
    return 'border-rose-200 bg-rose-50 text-rose-950';
  }

  if (snapshot.severity === 'warn') {
    return 'border-amber-200 bg-amber-50 text-amber-950';
  }

  return 'border-sky-200 bg-sky-50 text-sky-950';
}

function TreeMissingParentLifecycleOperationalPresentation({
  snapshot,
}: {
  snapshot:
    ReturnType<
      typeof useTreeRuntimeMissingParentLifecycleOperationalObservability
    >;
}) {
  const counts =
    snapshot.lifecycleCounts;

  return (
    <section
      data-testid="tree-missing-parent-lifecycle-operational-observability"
      data-lifecycle-observability-availability={
        snapshot.availability
      }
      className={`mt-5 rounded-[2rem] border p-6 ${lifecycleObservabilityToneClass(
        snapshot,
      )}`}
    >
      <p className="text-xs font-bold uppercase tracking-[0.18em]">
        Missing-parent lifecycle attribution
      </p>

      <h2 className="mt-2 text-3xl font-bold">
        {snapshot.availability === 'NOT_OBSERVED'
          ? 'Runtime evidence not observed'
          : snapshot.attributionStatus}
      </h2>

      <p className="mt-2 max-w-3xl text-sm">
        {snapshot.summary}
      </p>

      <div className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {[
          [
            'Availability',
            snapshot.availability,
          ],
          [
            'Attribution status',
            snapshot.attributionStatus,
          ],
          [
            'Owner-resolution mode',
            snapshot.browserOwnerResolutionMode,
          ],
          [
            'Evidence severity',
            snapshot.severity,
          ],
          [
            'Lifecycle counts published',
            snapshot.lifecycleCountsPublished,
          ],
          [
            'Observation revision',
            snapshot.revision,
          ],
          [
            'Observed at',
            snapshot.observedAt,
          ],
          [
            'Identity parity proven',
            snapshot.identityParityProven,
          ],
          [
            'Repair ready',
            snapshot.repairReady,
          ],
        ].map(([
          label,
          value,
        ]) => (
          <div
            key={String(label)}
            className="rounded-xl border border-current/20 bg-white/70 p-4"
          >
            <p className="text-xs font-bold uppercase tracking-wide opacity-70">
              {label}
            </p>

            <p className="mt-1 break-words text-sm font-bold">
              {factValue(
                value as
                  | string
                  | number
                  | boolean
                  | null,
              )}
            </p>
          </div>
        ))}
      </div>

      {counts ? (
        <div className="mt-5 rounded-2xl border border-current/20 bg-white/60 p-4">
          <p className="text-sm font-bold">
            Qualified aggregate lifecycle counts
          </p>

          <div className="mt-3 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {[
              [
                'Browser missing-parent occurrences',
                counts.browserMissingReferenceParentIdsCount,
              ],
              [
                'Server missing-parent occurrences',
                counts.serverMissingReferenceParentIdsCount,
              ],
              [
                'Soft-deleted same graph',
                counts.softDeletedSameGraphCount,
              ],
              [
                'Indeterminate',
                counts.indeterminateCount,
              ],
              [
                'Active same graph',
                counts.activeSameGraphCount,
              ],
              [
                'Cross graph',
                counts.crossGraphCount,
              ],
              [
                'Physically absent',
                counts.physicallyAbsentCount,
              ],
            ].map(([
              label,
              value,
            ]) => (
              <div
                key={String(label)}
                className="rounded-xl border border-current/20 bg-white/80 p-4"
              >
                <p className="text-xs font-bold uppercase tracking-wide opacity-70">
                  {label}
                </p>

                <p className="mt-1 text-sm font-bold">
                  {value}
                </p>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      <p className="mt-5 text-xs opacity-75">
        Read-only operational evidence. Aggregate lifecycle attribution does not prove member identity parity, authorize repair, alter Tree runtime decisions, or certify Tree health or release readiness.
      </p>
    </section>
  );
}

function TreeLiveOutput({
  value,
}: {
  value: any;
}) {
  return (
    <section className="mt-5 rounded-[2rem] border border-slate-200 bg-white p-6">
      <h2 className="text-3xl">
        Tree Governor Live Output
      </h2>

      <pre className="mt-4 overflow-auto whitespace-pre-wrap rounded-2xl bg-slate-950 p-4 text-sm text-slate-100">
        {value == null
          ? 'Waiting for governed Tree runtime evidence.'
          : JSON.stringify(value, null, 2)}
      </pre>
    </section>
  );
}

export default function TreeOperationsWorkspace({
  repairPlan,
  liveOutput,
  onSimulationChange,
}: TreeOperationsWorkspaceProps) {
  const registryEvidence =
    useCanonicalTreeRegistryEvidence();

  const runtimeConvergenceEvidence =
    useTreeRuntimeConvergenceEvidence();

  const geometryRuntimeEvidence =
    useTreeGeometryRuntimeEvidence();

  const legacyAncestryCausalEvidence =
    useTreeRuntimeLegacyAncestryCausalEvidence();

  const canonicalBranchCounterfactualEvidence =
    useTreeRuntimeCanonicalBranchCounterfactualEvidence();

  const branchLayoutInteractionEvidence =
    useTreeRuntimeBranchLayoutInteractionEvidence();

  const missingParentLifecycleObservability =
    useTreeRuntimeMissingParentLifecycleOperationalObservability();

  useEffect(
    () =>
      startCanonicalTreeRetirementReadinessAuthenticatedEvidencePublicationRuntimeCoordinator(),
    [],
  );

  const historicalAuthorityContainment =
    resolveContainedCanonicalTreeHistoricalAuthorityPresentation();

  const historicalAuthorityPresentation =
    historicalAuthorityContainment.presentation;

  const lastPublishedContainmentKey =
    useRef<string | null>(
      null,
    );

  useEffect(
    () => {
      if (
        historicalAuthorityContainment.status !==
        'FALLBACK'
      ) {
        lastPublishedContainmentKey.current =
          null;

        closeCanonicalTreeHistoricalAuthorityPresentationLifecycleEpisodes();

        return;
      }

      const publicationKey =
        [
          historicalAuthorityContainment.status,
          historicalAuthorityContainment.reason,
          historicalAuthorityContainment.failureMessage ??
            '',
        ].join('|');

      if (
        lastPublishedContainmentKey.current ===
        publicationKey
      ) {
        return;
      }

      lastPublishedContainmentKey.current =
        publicationKey;

      publishCanonicalTreeHistoricalAuthorityPresentationObservability({
        eventKind:
          'TREE_HISTORICAL_AUTHORITY_PRESENTATION_FALLBACK_ACTIVATED',

        reason:
          historicalAuthorityContainment.reason,

        failureMessage:
          historicalAuthorityContainment.failureMessage,
      });
    },
    [
      historicalAuthorityContainment.status,
      historicalAuthorityContainment.reason,
      historicalAuthorityContainment.failureMessage,
    ],
  );

  return (
    <div className="mt-5">
      <TreeSimulationWorkspace
        onSimulationChange={onSimulationChange}
      />

      <TreeRegistryLifecyclePresentation
        snapshot={registryEvidence}
      />

      <TreeRuntimeConvergencePresentation
        projection={
          runtimeConvergenceEvidence
        }
      />

      <TreeGeometryRuntimeEvidencePresentation
        projection={
          geometryRuntimeEvidence
        }
      />

      <TreeLegacyAncestryCausalEvidencePresentation
        projection={
          legacyAncestryCausalEvidence
        }
      />

      <TreeCanonicalBranchCounterfactualPresentation
        projection={
          canonicalBranchCounterfactualEvidence
        }
      />

      <TreeBranchLayoutInteractionPresentation
        projection={
          branchLayoutInteractionEvidence
        }
      />

      <TreeMissingParentLifecycleOperationalPresentation
        snapshot={
          missingParentLifecycleObservability
        }
      />

      <TreeHistoricalAuthorityPresentationErrorBoundary>
        <TreeHistoricalAuthorityPresentation
          presentation={
            historicalAuthorityPresentation
          }
        />
      </TreeHistoricalAuthorityPresentationErrorBoundary>

      <TreeStructuralRepairReview
        summary={repairPlan?.summary ?? null}
        grouping={repairPlan?.grouping ?? null}
        plan={repairPlan?.report ?? null}
        certification={
          repairPlan?.rendererCertification ??
          null
        }
        generatedAt={
          repairPlan?.generatedAt ?? null
        }
        effectiveOwnerUserId={
          repairPlan?.effectiveOwnerUserId ??
          null
        }
        source={repairPlan?.source ?? null}
        mode={repairPlan?.mode ?? null}
      />

      <TreeLiveOutput value={liveOutput} />
    </div>
  );
}
